import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { configService } from '../../services/configService';

interface ConfigInitializerProps {
  children: React.ReactNode;
}

/**
 * Component that initializes the application configuration before rendering children
 * This ensures config.json is loaded once at application startup
 */
const ConfigInitializer: React.FC<ConfigInitializerProps> = ({ children }) => {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    const initializeConfiguration = async () => {
      try {
        await configService.initialize();
        setIsConfigLoaded(true);
        setConfigError(null);
      } catch (error) {
        console.error('Failed to initialize configuration:', error);
        setConfigError(error instanceof Error ? error.message : 'Failed to load configuration');
        // Even if config fails, we'll still render the app with defaults
        setIsConfigLoaded(true);
      }
    };

    initializeConfiguration();
  }, []);

  // Show loading state while configuration is being loaded
  if (!isConfigLoaded) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading application configuration...
        </Typography>
      </Box>
    );
  }

  // Show error message if configuration failed to load (but still render app)
  if (configError) {
    console.warn('Configuration error (continuing with defaults):', configError);
  }

  return <>{children}</>;
};

export default ConfigInitializer;